import { z } from "zod";
import { CompetitionCategoryType } from "../CompetitionCategoryType.js";
import { Gender } from "../Gender.js";
import { Locale } from "../Locale.js";
import { TShirtSize } from "../TShirtSize.js";
import { Team } from "../Team.js";
import { competitionCategoryTypesWithRequiredAudio } from "../competitionCategoryTypesWithRequiredAudio.js";
import { competitionCategoryTypeNameMap } from "../names/competitionCategoryTypeNameMap.js";
import { dogInputSchema } from "./dogSchema.js";
import { handlerInputSchema } from "./handlerSchema.js";
import { mediaSchema } from "./mediaSchema.js";
import { nonEmptyString } from "./nonEmptyString.js";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
export function createApplicationCreateInputSchema(locale: Locale) {
	return z
		.object({
			audio: mediaSchema.optional(),
			competitionCategoryTypes: z.array(z.nativeEnum(CompetitionCategoryType)).min(1, {
				message: {
					[Locale.cs]: "Vyberte alespoň 1 disciplínu.",
					[Locale.en]: "Choose atleast one discipline.",
				}[locale],
			}),
			competitionId: nonEmptyString,
			dog: dogInputSchema,
			gdprApprovedAt: nonEmptyString,
			handler: handlerInputSchema,
			isHandlerOwnerOfDog: z.boolean(),
			recordingApprovedAt: nonEmptyString,
			tShirt: z
				.object({
					size: z.nativeEnum(TShirtSize),
					gender: z.nativeEnum(Gender),
				})
				.optional(),
			team: z.nativeEnum(Team).optional(),
		})
		.superRefine((val, ctx) => {
			const selectedCategoriesWhichRequireAudio = competitionCategoryTypesWithRequiredAudio.filter((type) =>
				val.competitionCategoryTypes.includes(type),
			);
			if (selectedCategoriesWhichRequireAudio.length > 0 && !val.audio) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["competitionCategoryTypes"],
					message: {
						[Locale.cs]: `Následující disciplíny vyžadují nahraný audio soubor: ${selectedCategoriesWhichRequireAudio.map((type) => competitionCategoryTypeNameMap[type][locale]).join(", ")}`,
						[Locale.en]: `Following disciplines require uploaded audio file: ${selectedCategoriesWhichRequireAudio.map((type) => competitionCategoryTypeNameMap[type][locale]).join(", ")}`,
					}[locale],
				});
			}
		});
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
export function createApplicationEditInputSchema(locale: Locale) {
	return z.object({
		audio: mediaSchema.optional(),
		categories: z
			.array(
				z.object({
					confirmed: z.boolean(),
					paid: z.number().min(0),
					price: z.number().min(0),
					topped: z.boolean(),
					type: z.nativeEnum(CompetitionCategoryType),
				}),
			)
			.min(1, {
				message: {
					[Locale.cs]: "Vyberte alespoň 1 disciplínu.",
					[Locale.en]: "Choose atleast one discipline.",
				}[locale],
			}),
		dogId: nonEmptyString,
		handlerId: nonEmptyString,
		note: nonEmptyString.optional(),
		tShirt: z
			.object({
				size: z.nativeEnum(TShirtSize),
				gender: z.nativeEnum(Gender),
			})
			.optional(),
		team: z.nativeEnum(Team).optional(),
	});
}
